/**
 * Sprint v1.6 Stufe 4A (Mi 02.09.2026) — Türöffner-Karte.
 *
 * Standard-Card mit 4 Feldern (Titel, Beschreibung, Beispiel, Aktion),
 * ausgerichtet auf den aktuellen Fokustyp des Users bzw. auf einen Ziel-
 * Fokustyp (Prop `slug`). Zeigt konkret, was der Türöffner für diesen
 * Fokus-Typ ist — kein abstraktes Metaphern-Wording, sondern anwendbar.
 *
 * Sprachprinzip v8: Alltagssprache, keine Anglizismen, keine Coach-Sprache.
 * Copy morgen früh (03.09.) durch Andreas redaktionell reviewt.
 */
import React from "react";
import { ArrowRight } from "lucide-react";
import { EuleGlyph, BieneGlyph, KatzeGlyph, HundGlyph } from "@/components/FokustypGlyphs";

const GLYPH = { eule: EuleGlyph, biene: BieneGlyph, katze: KatzeGlyph, hund: HundGlyph };
const NAME = { eule: "Eule", biene: "Biene", katze: "Katze", hund: "Hund" };

// Standard-Türöffner-Copy pro Fokustyp (Standard, v1.6 Stufe 4A).
const TUEROEFFNER_COPY = {
  eule: {
    beschreibung:
      "Sinn zuerst, dann Details. Die Eule öffnet, wenn du das große Bild lieferst — nicht den Plan.",
    beispiel:
      "\u201eBevor ich zum Plan komme: worum es mir wirklich geht — es geht darum, dass wir langfristig X sichern.\u201c",
    aktion_text: "Ersten Aufschlag für eine Eule formulieren",
  },
  biene: {
    beschreibung:
      "Struktur zuerst, dann alles andere. Die Biene öffnet, wenn du den Plan pro Schritt begründen kannst.",
    beispiel:
      "\u201eIch schlage vor: erstens X, weil Y. Zweitens Z, weil W. Passt das für dich?\u201c",
    aktion_text: "Ersten Aufschlag für eine Biene formulieren",
  },
  katze: {
    beschreibung:
      "Eigenwille zuerst. Die Katze öffnet, wenn du ihr etwas anbietest, was sie selbst reizt — nicht was sie muss.",
    beispiel:
      "\u201eDas hier reizt mich gerade: X. Wenn dich das auch reizt, lass uns weitermachen.\u201c",
    aktion_text: "Ersten Aufschlag für eine Katze formulieren",
  },
  hund: {
    beschreibung:
      "Verbindung zuerst. Der Hund öffnet, wenn er spürt, dass du ihn meinst — nicht nur die Sache.",
    beispiel:
      "\u201eMir ist wichtig, dass wir das zusammen hinkriegen. Was wäre dein nächster Schritt?\u201c",
    aktion_text: "Ersten Aufschlag für einen Hund formulieren",
  },
};

export default function TueroeffnerKarte({ slug, onAction }) {
  const active = slug && TUEROEFFNER_COPY[slug] ? slug : "eule";
  const copy = TUEROEFFNER_COPY[active];
  const Glyph = GLYPH[active];
  const name = NAME[active];

  return (
    <section
      className="rounded-2xl bg-white border border-slate-200 p-6 shadow-sm"
      data-testid="tueroeffner-karte"
      data-fokustyp={active}
    >
      <div className="flex items-start gap-4">
        {Glyph && (
          <div className="w-12 h-12 flex-shrink-0" data-testid="tueroeffner-karte-glyph">
            <Glyph size={48} />
          </div>
        )}
        <div className="flex-1 min-w-0">
          <p className="text-xs font-bold uppercase tracking-widest text-[var(--fts-primary-bright)]">
            Türöffner
          </p>
          <h3
            className="mt-1 text-lg sm:text-xl font-extrabold text-slate-900"
            data-testid="tueroeffner-karte-titel"
          >
            Der Türöffner für die {name}
          </h3>
          <p
            className="mt-2 text-sm text-slate-700 leading-relaxed"
            data-testid="tueroeffner-karte-beschreibung"
          >
            {copy.beschreibung}
          </p>
          <blockquote
            className="mt-3 rounded-xl bg-slate-50 border-l-4 border-[var(--fts-primary-bright)] px-4 py-3 text-sm text-slate-800 italic leading-relaxed"
            data-testid="tueroeffner-karte-beispiel"
          >
            {copy.beispiel}
          </blockquote>
          <button
            type="button"
            onClick={onAction}
            className="mt-4 inline-flex items-center gap-1.5 rounded-xl bg-[var(--fts-primary-bright)] hover:opacity-90 text-white font-bold text-sm px-4 py-2.5"
            data-testid="tueroeffner-karte-aktion"
          >
            {copy.aktion_text}
            <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </div>
    </section>
  );
}
